import { useMemo, type ReactNode } from 'react';
import { useLoaderData } from 'react-router';

import { EnquiryFormPageType } from 'generated/graphql';
import type { loader } from '~/routes/_app.project.$urlSegment._index';
import {
    PageCtasProvider,
    type PageBookIntentBase,
    type PageCtasProviderProps,
} from '~/modules/enquiry/component/PageCtasProvider';
import { CtaAutoOpen } from '~/modules/enquiry/component/CtaAutoOpen';
import type { EnquiryModalContext } from '~/modules/enquiry/hooks/use-enquiry-modal';

interface LinkableSession {
    ID: number;
    IsActive?: boolean | null;
    HasConnectedConsultant?: boolean | null;
}

interface ProjectSessions {
    ClientDirectorCalendarSessions?: (LinkableSession | null)[] | null;
    ClientDirectorCalendarSession?: LinkableSession | null;
}

const isBookable = (session: LinkableSession | null | undefined) =>
    Boolean(session?.IsActive && session.HasConnectedConsultant);

/**
 * The bookable Client Director sessions the project links to. Both one-on-one
 * and round-robin sessions count; only active sessions whose consultant has a
 * connected calendar do, since availability can return no slots otherwise.
 * The many_many `ClientDirectorCalendarSessions` come first, then the legacy
 * has_one `ClientDirectorCalendarSession` when it is not already listed.
 */
export const projectLinkedSessionIds = (project: ProjectSessions): number[] => {
    const ids = (project.ClientDirectorCalendarSessions ?? [])
        .filter(isBookable)
        .map((session) => (session as LinkableSession).ID);
    const legacy = project.ClientDirectorCalendarSession;
    if (legacy && isBookable(legacy) && !ids.includes(legacy.ID)) {
        ids.push(legacy.ID);
    }
    return ids;
};

/**
 * Resolves the project page's Enquire and Book a meeting buttons once, from the
 * loader's `pageCtas` and the project, for every CTA on the page to read.
 *
 * @param interactive - When false, skip mounting CtaAutoOpen so URL enquire/book
 *   params do not open in the page builder.
 */
export const ProjectPageCtasProvider = ({
    children,
    interactive = true,
}: {
    children: ReactNode;
    interactive?: boolean;
}) => {
    const { project, primaryCTA, pageCtas } = useLoaderData<typeof loader>();
    const professional = project.Professional;

    const fallback = useMemo<PageCtasProviderProps['fallback']>(
        () => ({
            pageType: EnquiryFormPageType.Project,
            enquiryAudience: professional.EnquiryAudience,
            enquireLabel: primaryCTA.primaryCTALabelTitleCase,
            enquireLabelShort: primaryCTA.primaryCTALabelShort,
            enquireLabelEnum: primaryCTA.primaryCTA,
            linkedSessionIds: projectLinkedSessionIds(project),
        }),
        [professional.EnquiryAudience, primaryCTA, project],
    );

    const enquiry = useMemo<EnquiryModalContext>(
        () => ({
            professional,
            itemType: 'project',
            siteTreeID: project.ID,
            // Names the project in the suggested message, as product and article pages do.
            itemTitle: project.Title,
            relatedItem: {
                id: project.ID,
                type: 'project',
                title: project.Title,
                category: project.categoryPath,
            },
        }),
        [professional, project.ID, project.Title, project.categoryPath],
    );

    const book = useMemo<PageBookIntentBase>(
        () => ({
            professionalUrlSegment: professional.URLSegment,
            siteTreeId: project.ID,
            professionalId: professional.ID,
            itemTitle: project.Title,
            professionalName: professional.Title,
            companyLogo: professional.CompanyLogo,
            logoBackgroundColor: professional.LogoBackgroundColor,
        }),
        [professional, project.ID, project.Title],
    );

    return (
        <PageCtasProvider
            loaded={pageCtas}
            fallback={fallback}
            enquiry={enquiry}
            book={book}
        >
            {children}
            {interactive ? <CtaAutoOpen /> : null}
        </PageCtasProvider>
    );
};
